import { useContext, useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import SessionContext from '../../../../library/Core/SessionContext';
import InquiryReportService from '../InquiryReportService';

interface InquiryDetailModalProps {
    inquiry: any;
    show: boolean;
    onSaved?: () => Promise<void> | void;
    onHide: () => void;
}

const formatDateTime = (value?: string) => {
    if (!value) return '-';
    try {
        return new Intl.DateTimeFormat('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(new Date(value));
    } catch (error) {
        return value;
    }
};

const InquiryDetailModal = ({ inquiry, show, onSaved, onHide }: InquiryDetailModalProps) => {
    const { sessionState } = useContext(SessionContext);
    const [status, setStatus] = useState('');
    const [outcome, setOutcome] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [messages, setMessages] = useState<any[]>([]);
    const [draftMessage, setDraftMessage] = useState('');
    const [isMessagesLoading, setIsMessagesLoading] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const threadRef = useRef<HTMLDivElement | null>(null);
    const currentUserId = sessionState?.studentId ?? sessionState?.userId ?? sessionState?.id;

    const isCurrentUserMessage = (item: any) => {
        if (item?.senderUserId && currentUserId) {
            return item.senderUserId === currentUserId;
        }

        return item?.senderType === 'PROVIDER';
    };

    useEffect(() => {
        if (!inquiry) return;
        setStatus(inquiry.status || 'NEW');
        setOutcome(inquiry.outcome || 'PENDING');
        setDraftMessage('');
    }, [inquiry]);

    useEffect(() => {
        if (!show || !inquiry?.id) return;

        let isMounted = true;

        const loadMessages = async () => {
            setIsMessagesLoading(true);
            try {
                const response = await InquiryReportService.getMessages(inquiry.id);
                if (!isMounted) return;
                setMessages(response?.data?.data ?? []);
            } catch (error: any) {
                if (!isMounted) return;
                console.error('Inquiry messages error:', error);
                setMessages([]);
                toast.error(error?.response?.data?.message || 'Failed to load conversation');
            } finally {
                if (isMounted) setIsMessagesLoading(false);
            }
        };

        loadMessages();

        return () => {
            isMounted = false;
        };
    }, [show, inquiry?.id]);

    useEffect(() => {
        if (!threadRef.current) return;
        threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }, [messages, show]);

    const handleSendMessage = async () => {
        const trimmedMessage = draftMessage.trim();
        if (!inquiry?.id || !trimmedMessage || isSendingMessage) return;

        const tempId = `temp-${Date.now()}`;
        const optimisticMessage = {
            id: tempId,
            senderType: 'PROVIDER',
            message: trimmedMessage,
            createdAt: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, optimisticMessage]);
        setDraftMessage('');
        setIsSendingMessage(true);

        try {
            const response = await InquiryReportService.sendMessage(inquiry.id, { message: trimmedMessage });
            const savedMessage = response?.data?.data;

            setMessages((prev) => prev.map((item) => (item.id === tempId ? savedMessage || item : item)));
            setStatus('RESPONDED');

            if (onSaved) {
                await onSaved();
            }
        } catch (error: any) {
            console.error('Inquiry send message error:', error);
            setMessages((prev) => prev.filter((item) => item.id !== tempId));
            setDraftMessage(trimmedMessage);
            toast.error(error?.response?.data?.message || 'Failed to send message');
        } finally {
            setIsSendingMessage(false);
        }
    };

    const handleSave = async () => {
        if (!inquiry?.id || isSaving) return;

        setIsSaving(true);
        try {
            await InquiryReportService.updateInquiry(inquiry.id, {
                status,
                outcome,
            });

            if (onSaved) {
                await onSaved();
            }

            toast.success('Inquiry updated successfully');
            onHide();
        } catch (error: any) {
            console.error('Inquiry update error:', error);
            toast.error(error?.response?.data?.message || 'Failed to update inquiry');
        } finally {
            setIsSaving(false);
        }
    };

    if (!inquiry) return null;

    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Body className="p-4 p-md-5">
                <h5 className="font-30 font-600 mb-3">Inquiry Details</h5>
                <a
                    href="javascript:;"
                    className="position-absolute top-20 right-20 m-0 text-secondary font-30"
                    onClick={onHide}
                    aria-label="Close">
                    <i className="bi bi-x-circle-fill"></i>
                </a>

                <div className="bg-gray-F2F4F7 rounded p-4 mb-4">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="font-14 text-muted mb-1">Inquirer</div>
                            <div className="font-16 font-600">{inquiry.inquirerName || '-'}</div>
                            <div className="font-14">{inquiry.inquirerEmail || '-'}</div>
                        </div>
                        <div className="col-md-6">
                            <div className="font-14 text-muted mb-1">Received On</div>
                            <div className="font-16 font-600">{formatDateTime(inquiry.createdAt)}</div>
                        </div>
                    </div>
                </div>

                <div className="mb-4">
                    <div className="font-18 font-600 mb-2">Subject</div>
                    <div className="font-16">{inquiry.subject || '-'}</div>
                </div>

                <div className="mb-4">
                    <div className="font-18 font-600 mb-2">Message</div>
                    <div
                        className="border rounded p-3 bg-light min-height-120 overflow-auto"
                        style={{ whiteSpace: 'normal', wordBreak: 'break-word', maxHeight: '280px' }}
                        dangerouslySetInnerHTML={{ __html: inquiry.message || '-' }}></div>
                </div>

                <div className="mb-4">
                    <div className="font-18 font-600 mb-2">Conversation</div>
                    <div ref={threadRef} className="border rounded p-3 bg-light overflow-auto" style={{ maxHeight: '320px' }}>
                        {isMessagesLoading ? (
                            <div className="text-muted font-14">Loading conversation...</div>
                        ) : messages.length === 0 ? (
                            <div className="text-muted font-14">No replies yet.</div>
                        ) : (
                            <div className="d-flex flex-column gap-3">
                                {messages.map((item) => {
                                    const isOwnMessage = isCurrentUserMessage(item);
                                    return (
                                        <div key={item.id} className={`d-flex ${isOwnMessage ? 'justify-content-end' : 'justify-content-start'}`}>
                                            <div className={`rounded px-3 py-2 ${isOwnMessage ? 'bg-skyblue text-white' : 'bg-white border'}`} style={{ maxWidth: '75%' }}>
                                                <div className="font-14" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.message || '-'}</div>
                                                <div className={`font-12 mt-1 ${isOwnMessage ? 'text-white-50' : 'text-muted'}`}>{formatDateTime(item.createdAt)}</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="row g-4 mb-4">
                    <div className="col-md-6">
                        <label className="form-label font-14 font-500">Status</label>
                        <select className="form-select" value={status} onChange={(event) => setStatus(event.target.value)}>
                            <option value="NEW">NEW</option>
                            <option value="VIEWED">VIEWED</option>
                            <option value="RESPONDED">RESPONDED</option>
                            <option value="CLOSED">CLOSED</option>
                        </select>
                    </div>
                    <div className="col-md-6">
                        <label className="form-label font-14 font-500">Outcome</label>
                        <select
                            className="form-select"
                            value={outcome}
                            onChange={(event) => setOutcome(event.target.value)}>
                            <option value="PENDING">PENDING</option>
                            <option value="CONVERTED">CONVERTED</option>
                            <option value="NOT_TAKEN_UP">NOT_TAKEN_UP</option>
                            <option value="DECLINED">DECLINED</option>
                        </select>
                    </div>
                </div>

                <div className="mb-4">
                    <label className="form-label font-14 font-500">Type your reply...</label>
                    <textarea
                        className="form-control h-auto"
                        rows={4}
                        placeholder="Type your response here..."
                        value={draftMessage}
                        onChange={(event) => setDraftMessage(event.target.value)}
                    />
                </div>

                <div className="d-flex justify-content-between gap-2 flex-wrap">
                    <button type="button" className="btn btn-skyblue height-40 px-3 text-white" onClick={handleSendMessage} disabled={isSendingMessage || !draftMessage.trim()}>
                        {isSendingMessage ? 'Sending...' : 'Send'}
                    </button>
                    <div className="d-flex justify-content-end gap-2">
                    <button type="button" className="btn btn-border-gray height-40" onClick={onHide}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-green height-40" onClick={handleSave} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save Updates'}
                    </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default InquiryDetailModal;